import { supabase } from '@/integrations/supabase/client';
import { User, Session } from '@supabase/supabase-js';
import { UserRole } from '@/types';

export interface AuthState {
  user: User | null;
  session: Session | null;
  loading: boolean;
  userRole: UserRole | null;
}

export const signUp = async (email: string, password: string, captchaToken?: string) => {
  const redirectUrl = `${window.location.origin}/`;
  
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      emailRedirectTo: redirectUrl,
      captchaToken
    }
  });
  
  return { data, error };
};

export const signIn = async (email: string, password: string, captchaToken?: string) => {
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
    options: {
      captchaToken
    }
  });
  
  return { data, error };
};

export const signOut = async () => {
  const { error } = await supabase.auth.signOut();
  return { error };
};

export const resetPassword = async (email: string, captchaToken?: string) => {
  // Use the current domain with a reset flag that will survive the Supabase redirect
  const redirectUrl = `${window.location.origin}/auth?reset=true`;
  
  console.log('Password reset redirect URL:', redirectUrl);
  
  const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: redirectUrl,
    captchaToken
  });
  
  return { data, error };
};

export const createProfile = async (userId: string, role: UserRole) => {
  // Get current session to ensure user is authenticated
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) {
    throw new Error('User must be authenticated to create profile');
  }
  
  // Check if profile already exists
  const { data: existingProfile } = await supabase
    .from('profiles')
    .select('user_id')
    .eq('user_id', session.user.id)
    .maybeSingle();
  
  if (!existingProfile) {
    console.log('Creating new profile record');
    // Create profile record (kept for backward compatibility)
    const { error: profileError } = await supabase
      .from('profiles')
      .insert({
        user_id: session.user.id
      });
      
    if (profileError) {
      console.error('Error creating profile:', profileError);
      throw profileError;
    }
  } else {
    console.log('Profile already exists, checking role');
  }

  // Check if user role already exists
  const { data: existingRole } = await supabase
    .from('user_roles')
    .select('user_id')
    .eq('user_id', session.user.id)
    .maybeSingle();
    
  if (existingRole) {
    console.log('User role already exists, skipping creation');
    return { data: existingRole, error: null };
  }

  // Create user role in separate table for security
  console.log('Creating user_roles entry for:', session.user.id, 'with role:', role);
  const { data, error } = await supabase
    .from('user_roles')
    .insert({
      user_id: session.user.id,
      role
    })
    .select()
    .single();
  
  console.log('Insert result - data:', data, 'error:', error);
  
  if (error) {
    console.error('Error creating user role:', error);
    throw error;
  }
  
  if (!data) {
    console.error('No data returned from user_roles insert');
    throw new Error('Failed to create user role - no data returned');
  }
  
  // Verify role was created successfully
  const { data: roleCheck, error: verifyError } = await supabase
    .from('user_roles')
    .select('role')
    .eq('user_id', session.user.id)
    .single();
    
  console.log('Role verification - data:', roleCheck, 'error:', verifyError);
    
  return { data, error: null };
};

export const createSpecialistProfile = async (userId: string, profileData: {
  first_name?: string;
  last_name?: string;
  phone?: string;
  address_line_1?: string;
  address_line_2?: string;
  city?: string;
  county?: string;
  postcode?: string;
  building_no?: string;
  years_exp?: number;
  tags?: string[];
  qualifications?: string[];
}) => {
  // Get current session to ensure user is authenticated
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) {
    throw new Error('User must be authenticated to create profile');
  }

  // Only include fields that have values
  const filteredData = Object.fromEntries(
    Object.entries(profileData).filter(([_, value]) => {
      if (value === undefined || value === null || value === '') return false;
      if (Array.isArray(value) && value.length === 0) return false;
      return true;
    })
  );
  
  const { data, error } = await supabase
    .from('specialist_profiles')
    .insert({
      user_id: session.user.id, // Use session user id instead of parameter
      onboarding_completed_at: null, // Ensure onboarding flow is triggered
      onboarding_current_step: 1,
      ...filteredData
    });
    
  return { data, error };
};

export const createPartnerProfile = async (userId: string, profileData: {
  business_name?: string;
  hq_address_line_1?: string;
  hq_address_line_2?: string;
  hq_city?: string;
  hq_county?: string;
  hq_postcode?: string;
  site_manager_name?: string;
  site_manager_phone?: string;
  site_manager_email?: string;
  site_id?: string;
  invited_by_manager_id?: string;
}) => {
  // Get current session to ensure user is authenticated
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) {
    throw new Error('User must be authenticated to create profile');
  }

  // Only include fields that have values
  const filteredData = Object.fromEntries(
    Object.entries(profileData).filter(([_, value]) => value !== undefined && value !== null && value !== '')
  );
  
  const { data, error } = await supabase
    .from('partner_profiles')
    .insert({
      user_id: session.user.id, // Use session user id instead of parameter
      onboarding_completed_at: null, // Ensure onboarding flow is triggered
      onboarding_current_step: 1,
      ...filteredData
    });
    
  return { data, error };
};

export const createManagerProfile = async (userId: string, profileData: {
  company_name?: string;
  hq_address_line_1?: string;
  hq_address_line_2?: string;
  hq_city?: string;
  hq_county?: string;
  hq_postcode?: string;
  contact_name?: string;
  contact_phone?: string;
  contact_email?: string;
  invited_by?: string;
}) => {
  // Get current session to ensure user is authenticated
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) {
    throw new Error('User must be authenticated to create profile');
  }

  // Only include fields that have values
  const filteredData = Object.fromEntries(
    Object.entries(profileData).filter(([_, value]) => value !== undefined && value !== null && value !== '')
  );
  
  const { data, error } = await supabase
    .from('manager_profiles')
    .insert({
      user_id: session.user.id,
      onboarding_completed_at: null,
      onboarding_current_step: 1,
      ...filteredData
    });
    
  return { data, error };
};

export const getUserProfile = async (userId: string) => {
  console.log("getUserProfile: Fetching profile for userId:", userId);
  
  try {
    // Fetch role from secure user_roles table
    const { data, error } = await supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', userId)
      .maybeSingle();
      
    console.log("getUserProfile: Raw response:", { data, error });
    
    if (error) {
      console.error("getUserProfile: Database error:", error);
    }
    
    if (!data) {
      console.log("getUserProfile: No role found, user may need to complete signup");
    }
    
    return { data, error };
  } catch (err) {
    console.error("getUserProfile: Unexpected error:", err);
    return { data: null, error: err };
  }
};